<template>
    <div class="app-content-builder content-builder grid">
        <content-builder-nav class="col-12">
            <template v-slot:navigation>
                <slot name="navigation"></slot>
            </template>
        </content-builder-nav>
        <div v-show="status"
            class="loader col-12">
            <img src="/img/loader.gif">
        </div>
        <slot name="sidebar">
            <content-builder-sidebar class="col-12 col-lg-3"></content-builder-sidebar>
        </slot>
        <slot name="main">
            <content-builder-editor class="col-12 col-lg-9"></content-builder-editor>
        </slot>
        <content-builder-data-generator></content-builder-data-generator>
        <content-builder-ai-relay></content-builder-ai-relay>
    </div>
</template>

<script>
import _ from 'lodash'
import { mapState, mapGetters } from 'vuex'
import ContentBuilderNav from './ContentBuilderNav.vue'
import ContentBuilderDataGenerator from './ContentBuilderDataGenerator.vue'
import ContentBuilderSidebar from './ContentBuilderSidebar.vue'
import ContentBuilderEditor from './ContentBuilderEditor.vue'
import ContentBuilderAiRelay from './ContentBuilderAiRelay.vue'
import Echo from '../helpers/echo'

const REFRESH_INTERVAL = 300000 // 5 minutes

export default {
    components: {
        ContentBuilderDataGenerator,
        ContentBuilderEditor,
        ContentBuilderNav,
        ContentBuilderSidebar,
        ContentBuilderAiRelay,
    },
    props: {
        project: {
            type: Object,
            default: null
        },
        learningObject: {
            type: Object,
            default: null
        },
        settings: {
            type: Object,
            default: null
        }
    },
    data () {
        return { }
    },
    computed: {
        ...mapState('builder', {
            screenTemplateMode: state => state.screenTemplateMode,
            status: state => state.status
        }),
        ...mapGetters('builder', [
            'currentDataObject',
            'schemaRefs',
            'changes'
        ])
    },
    methods: {
        async refresh () {
            const result = await this.$store.dispatch('builder/refresh')
            if (result) {
                _.delay(() => { this.refresh() }, REFRESH_INTERVAL)
            }
        }
    },
    created () {
        if (this.project) {
            this.$store.dispatch('builder/initialize', {
                source: this.project,
                settings: this.settings,
                type: 'project'
            })
        } else {
            this.$store.dispatch('builder/initialize', {
                source: this.learningObject,
                settings: this.settings,
                type: 'learning'
            })
        }

        // set variable to prevent screen template behavior in screen template generation
        this.$store.commit('builder/setScreenTemplateMode', false)

        // listen for hash change
        window.addEventListener('hashchange', (e) => {
            // console.log('The hash has changed!', e)
            const id = parseInt(window.location.hash.substr(1), 10)
            if (id) {
                this.$store.commit('builder/setSelectedId', id)
            }
        }, false)

        window.addEventListener('beforeunload', (e) => {
            if (this.$store.getters['builder/dirty']) {
                e.returnValue = 'Are you sure you want to leave? Any unsaved changes will be lost.'
            }
        })

        // join presence channel
        console.info(`Joining Presence Channel: ${this.$store.getters['builder/presenceChannel']}`)
        Echo.join(this.$store.getters['builder/presenceChannel'])
            .here((users) => {
                this.$store.commit('builder/setUsers', users)
            })
            .joining((user) => {
                this.$store.commit('builder/addUser', user)
            })
            .leaving((user) => {
                this.$store.commit('builder/removeUser', user)
            })

        // join object notification channel
        console.info(`Joining Notification Channel: ${this.$store.getters['builder/notificationChannel']}`)
        Echo.private(this.$store.getters['builder/notificationChannel'])
            .notification((notification) => {
                this.$store.dispatch('builder/processNotification', notification)
            })

        // start refresh interval
        this.refresh()
    },
    mounted () {
        // check for initial selection
        const id = parseInt(window.location.hash.substr(1), 10)
        const objects = this.$store.state.builder.dataObjects
        const index = _.max([_.findIndex(objects, { id }), 0])
        if (objects[index]) {
            this.$store.dispatch('builder/selectObject', id)
        }
    }
}
</script>
